import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Delete, Refresh } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { localization, hasPermission } from '../../sc-utilities';
import * as actions from './store/diagnoseActions';
import Toolbar from '../../sc-utilities/Toolbar';
import * as action from "../../np-core/Auth/store/authActions";
import NICDelete from '../../sc-productComponents/NIC/NICDelete';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const DiagnoseList = props => {
    const { classes, translations, onFetchDiagnoseCount, onFetchDiagnoseList, fetching, saveColumnsFilter, activeCustomerID, tableColumns, tableRef, productID, ProductType, permissions, onClearDiagnoseResultsPerDiagnoseCases } = props;

    const [deleteModalState, setDeleteModalState] = useState({ open: false, ids: [] });

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selectionProps: rowData => {
            let disabled = false;
            if (rowData.Deleted) {
                disabled = true;
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        selection: hasPermission(permissions, "system_diagnose_case_round_results_clear"),
        search: false,
        minBodyHeight: window.innerHeight - 330 < 300 ? 300 : window.innerHeight - 330,
        maxBodyHeight: window.innerHeight - 330,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const handleBulkDelete = ids => {
        if (!ids) {
            setDeleteModalState({ open: false, ids: [] });
            return;
        }
        setDeleteModalState({ open: true, ids });
    };

    const tableActions = [
        {
            tooltip: translations["clear_diagnose_results"] || "Clear diagnose results",
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(data => data.DiagnoseCaseID)),
            position: 'toolbarOnSelect',
        }
    ]

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        const queryToSend = {...query};
        const columnsIndex = queryToSend.filters.findIndex(filter => filter.column.title === 'Status')
        if (columnsIndex !== -1 && queryToSend.filters[columnsIndex]?.value?.findIndex(status => status === 'notperformed') !== -1) {
            let statuses = queryToSend.filters[columnsIndex]?.value 
            statuses = statuses.filter(status => status !== 'notperformed');
            statuses.push(...['started', 'inprogress', 'notstarted']);
            queryToSend.filters[columnsIndex].value = statuses;
        }
        const q = filterQuery(queryToSend.filters);
        queryToSend['ActiveCustomerID'] = activeCustomerID;
        queryToSend['ProductID'] = productID ?? null;
        q["ProductType"] = ProductType;
        queryToSend['q'] = q;
        delete queryToSend["filters"];
        delete queryToSend["orderDirection"];
        delete queryToSend["search"];
        delete queryToSend["totalCount"];
        const count = await onFetchDiagnoseCount(queryToSend);
        const result = count ? await onFetchDiagnoseList(queryToSend) : [];
        setTableOptions({ ...tableOptions, pageSize: queryToSend['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: queryToSend.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                <Typography className={classes.typography}>{translations['diagnose'] || 'Diagnose'}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`Diagnose`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    const handleCloseDanger = async () => {
        await onClearDiagnoseResultsPerDiagnoseCases({ ProductID: productID, DiagnoseCaseIDs: deleteModalState.ids });
        handleBulkDelete();
        tableRef.current && tableRef.current.onQueryChange();
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} classes={{ toolbar: classes.toolbar }} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
                actions={tableActions}
            />
            {hasPermission(permissions, "diagnose_case_round_results_clear") ? (
                <NICDelete
                    activeCustomer={activeCustomerID}
                    entityName={translations["diagnose_results"] || "Diagnose results"}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleDelete={handleBulkDelete}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchDiagnoseCount: (filter, options) => dispatch(actions.fetchDiagnoseCount(filter, options)),
        onFetchDiagnoseList: (filter, options) => dispatch(actions.fetchDiagnoseList(filter, options)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onClearDiagnoseResultsPerDiagnoseCases: data => dispatch(actions.clearDiagnoseResultsPerDiagnoseCases(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(DiagnoseList));
